import { type ReactElement } from 'react';
import { s, typographyTokens } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { Box, Link, Text } from '@/ui/primitives';
import { tableV2NamePrimaryFontSize } from './TableV2Constants';
import { getTableV2LinkRoutingProps } from './TableV2LinkRouting';
import {
  tableV2NameCellContentSx,
  tableV2SingleLineNameStackSx,
  tableV2TwoLineNameStackSx,
} from './TableV2NameCellStyles';

export const TableV2NameCell = ({
  href,
  primary,
  secondary,
  secondaryHref,
}: {
  href?: string | null;
  primary: string;
  secondary?: string | null;
  secondaryHref?: string | null;
}): ReactElement => {
  const hasSecondary = secondary !== null && secondary !== undefined && secondary.length > 0;

  return (
    <Box sx={tableV2NameCellContentSx}>
      <Box
        sx={[
          { display: 'grid', gap: s('none'), minWidth: 0, overflow: 'hidden' },
          hasSecondary ? tableV2TwoLineNameStackSx : tableV2SingleLineNameStackSx,
        ]}
      >
        <Text
          role="body"
          component="div"
          sx={{
            '&.MuiTypography-root': {
              fontSize: tableV2NamePrimaryFontSize,
            },
            fontSize: tableV2NamePrimaryFontSize,
            fontWeight: typographyTokens.body.fontWeight,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {href ? (
            <OverflowTooltip title={primary} inline>
              <Link
                {...getTableV2LinkRoutingProps(href)}
                sx={{
                  '&.MuiTypography-root': {
                    fontSize: tableV2NamePrimaryFontSize,
                  },
                  color: 'text.primary',
                  display: 'inline-block',
                  fontSize: tableV2NamePrimaryFontSize,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textDecorationColor: 'currentColor',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {primary}
              </Link>
            </OverflowTooltip>
          ) : (
            primary
          )}
        </Text>
        {hasSecondary ? (
          <Text
            role="captionSmall"
            color="textSecondary"
            component="div"
            sx={{
              fontSize: (theme) => theme.typography.caption.fontSize,
              lineHeight: 1.35,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {secondaryHref ? (
              <OverflowTooltip title={secondary} inline>
                <Link
                  {...getTableV2LinkRoutingProps(secondaryHref)}
                  sx={{
                    color: 'text.secondary',
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textDecorationColor: 'currentColor',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondary}
                </Link>
              </OverflowTooltip>
            ) : (
              <OverflowTooltip title={secondary} inline>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondary}
                </Box>
              </OverflowTooltip>
            )}
          </Text>
        ) : null}
      </Box>
    </Box>
  );
};
